import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { adminApi } from '../../services/api';
import { Save, Gamepad2, MessageSquare, Trophy, Flame, Target, Plus, Trash2, Loader2, ExternalLink } from 'lucide-react';
import { PageTooltip } from '../components/TooltipSystem';

const GAME_LIST = [
  { key: 'wordle', name: 'Wordle', desc: 'Indovina la parola in 6 tentativi' },
  { key: 'trivia', name: 'Trivia', desc: '5 domande a risposta multipla' },
  { key: 'tango', name: 'Tango', desc: 'Puzzle griglia con tacos e burritos' },
];

export default function AdminGames() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testLink, setTestLink] = useState(null);

  const baseUrl = window.location.origin;

  useEffect(() => {
    adminApi.get('/admin/games/settings')
      .then(r => {
        setData(r.data);
        if (r.data.test_link) setTestLink(r.data.test_link);
      })
      .catch(() => toast.error('Errore caricamento impostazioni giochi'))
      .finally(() => setLoading(false));
  }, []);

  const openGameTest = (gameKey) => {
    if (!testLink) {
      toast.error('Nessun cliente di test trovato. Registra almeno un cliente.');
      return;
    }
    const url = `${baseUrl}/my/${testLink.token}?pin=${testLink.pin}&tab=giochi`;
    window.open(url, '_blank');
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await adminApi.put('/admin/games/settings', data);
      toast.success('Impostazioni giochi salvate!');
    } catch { toast.error('Errore nel salvataggio'); }
    finally { setSaving(false); }
  };

  const updateVisibility = (key) => {
    setData(d => ({ ...d, games_visibility: { ...d.games_visibility, [key]: !d.games_visibility[key] } }));
  };

  const updatePromo = (field, value) => {
    setData(d => ({ ...d, game_promo: { ...d.game_promo, [field]: value } }));
  };

  const updateStreak = (idx, field, value) => {
    setData(d => {
      const sr = [...d.streak_rewards];
      sr[idx] = { ...sr[idx], [field]: field === 'days' || field === 'points' ? parseInt(value) || 0 : value };
      return { ...d, streak_rewards: sr };
    });
  };

  const addStreak = () => {
    setData(d => ({ ...d, streak_rewards: [...d.streak_rewards, { days: 0, points: 0, badge: '', reward: '' }] }));
  };

  const removeStreak = (idx) => {
    setData(d => ({ ...d, streak_rewards: d.streak_rewards.filter((_, i) => i !== idx) }));
  };

  const updateMilestone = (idx, field, value) => {
    setData(d => {
      const mb = [...d.milestone_badges];
      mb[idx] = { ...mb[idx], [field]: field === 'threshold' ? parseInt(value) || 0 : value };
      return { ...d, milestone_badges: mb };
    });
  };

  const addMilestone = () => {
    setData(d => ({ ...d, milestone_badges: [...d.milestone_badges, { threshold: 0, badge: '' }] }));
  };

  const removeMilestone = (idx) => {
    setData(d => ({ ...d, milestone_badges: d.milestone_badges.filter((_, i) => i !== idx) }));
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-[#D44E20] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="p-6 max-w-3xl mx-auto">
      <PageTooltip page="games" />
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[#1A1A1A]">Giochi</h1>
          <p className="text-[#64748B] text-sm mt-1">Configura giochi, badge, streak e messaggi promozionali</p>
        </div>
        <button onClick={handleSave} disabled={saving} data-testid="save-games-settings"
          className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold text-white transition-all active:scale-95 disabled:opacity-50"
          style={{ backgroundColor: '#D44E20' }}>
          {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
          {saving ? 'Salvataggio...' : 'Salva'}
        </button>
      </div>

      <div className="space-y-5">
        {/* 1. Visibility toggles */}
        <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm">
          <h3 className="font-semibold text-[#1A1A1A] mb-1 flex items-center gap-2"><Gamepad2 size={16} /> Visibilità giochi</h3>
          <p className="text-[#64748B] text-xs mb-4">Scegli quali giochi mostrare sulla bacheca del cliente</p>
          {GAME_LIST.map(game => (
            <div key={game.key} className="flex items-center justify-between py-3 border-b border-gray-50 last:border-0">
              <div>
                <p className="text-[#1A1A1A] text-sm font-medium">{game.name}</p>
                <p className="text-[#64748B] text-xs mt-0.5">{game.desc}</p>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => openGameTest(game.key)} data-testid={`game-test-${game.key}`}
                  className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium text-[#D44E20] bg-[#D44E20]/8 hover:bg-[#D44E20]/15 transition-colors">
                  <ExternalLink size={12} /> Prova
                </button>
                <button onClick={() => updateVisibility(game.key)} data-testid={`game-toggle-${game.key}`}
                  className={`w-12 h-7 rounded-full transition-all relative ${data.games_visibility[game.key] ? 'bg-[#D44E20]' : 'bg-gray-300'}`}>
                  <div className={`w-5 h-5 bg-white rounded-full absolute top-1 transition-all shadow-sm ${data.games_visibility[game.key] ? 'left-6' : 'left-1'}`} />
                </button>
              </div>
            </div>
          ))}
          {testLink && (
            <p className="text-[10px] text-[#aaa] mt-3 pt-2 border-t border-gray-50">
              Test come: <span className="font-medium text-[#64748B]">{testLink.name}</span> (PIN: {testLink.pin})
            </p>
          )}
        </div>

        {/* 2. Promo messages */}
        <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm">
          <h3 className="font-semibold text-[#1A1A1A] mb-1 flex items-center gap-2"><MessageSquare size={16} /> Messaggi promozionali</h3>
          <p className="text-[#64748B] text-xs mb-4">Testi mostrati ai clienti durante e dopo i giochi</p>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-[#1A1A1A] mb-1">Banner durante il gioco</label>
              <textarea value={data.game_promo.banner_text} onChange={e => updatePromo('banner_text', e.target.value)}
                placeholder="Es: Vieni a trovarci per un Taco al Pastor speciale!" data-testid="promo-banner-text" rows={2}
                className="w-full bg-slate-50 border border-slate-200 text-[#1A1A1A] rounded-xl px-3 py-2 text-sm focus:border-[#D44E20] focus:outline-none resize-none" />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#1A1A1A] mb-1">Messaggio di completamento</label>
              <textarea value={data.game_promo.completion_text} onChange={e => updatePromo('completion_text', e.target.value)}
                placeholder="Es: Torna domani per mantenere la tua streak!" data-testid="promo-completion-text" rows={2}
                className="w-full bg-slate-50 border border-slate-200 text-[#1A1A1A] rounded-xl px-3 py-2 text-sm focus:border-[#D44E20] focus:outline-none resize-none" />
            </div>
          </div>
        </div>

        {/* 3. Streak rewards */}
        <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm">
          <div className="flex items-center justify-between mb-1">
            <h3 className="font-semibold text-[#1A1A1A] flex items-center gap-2"><Flame size={16} className="text-orange-500" /> Premi Streak</h3>
            <button onClick={addStreak} data-testid="add-streak" className="text-xs font-medium text-[#D44E20] flex items-center gap-1 hover:underline"><Plus size={14} /> Aggiungi</button>
          </div>
          <p className="text-[#64748B] text-xs mb-4">Definisci i premi assegnati al raggiungimento di ogni streak</p>

          {data.streak_rewards.length === 0 && <p className="text-[#64748B] text-sm text-center py-4">Nessun premio streak configurato</p>}

          <div className="space-y-3">
            {data.streak_rewards.map((sr, idx) => (
              <div key={idx} className="bg-gray-50 rounded-xl p-4 border border-slate-100" data-testid={`streak-row-${idx}`}>
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs font-semibold text-[#1A1A1A] flex items-center gap-1">
                    <Flame size={12} className="text-orange-500" /> Streak #{idx + 1}
                  </span>
                  <button onClick={() => removeStreak(idx)} className="text-red-400 hover:text-red-600"><Trash2 size={14} /></button>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-[10px] font-medium text-[#64748B] uppercase">Giorni</label>
                    <input type="number" value={sr.days} onChange={e => updateStreak(idx, 'days', e.target.value)}
                      className="w-full mt-1 bg-white border border-slate-200 rounded-lg px-2.5 py-1.5 text-sm text-[#1A1A1A] focus:border-[#D44E20] focus:outline-none" />
                  </div>
                  <div>
                    <label className="text-[10px] font-medium text-[#64748B] uppercase">Punti bonus</label>
                    <input type="number" value={sr.points} onChange={e => updateStreak(idx, 'points', e.target.value)}
                      className="w-full mt-1 bg-white border border-slate-200 rounded-lg px-2.5 py-1.5 text-sm text-[#1A1A1A] focus:border-[#D44E20] focus:outline-none" />
                  </div>
                  <div>
                    <label className="text-[10px] font-medium text-[#64748B] uppercase">Nome badge</label>
                    <input type="text" value={sr.badge} onChange={e => updateStreak(idx, 'badge', e.target.value)}
                      placeholder="es: streak_3" className="w-full mt-1 bg-white border border-slate-200 rounded-lg px-2.5 py-1.5 text-sm text-[#1A1A1A] focus:border-[#D44E20] focus:outline-none" />
                  </div>
                  <div>
                    <label className="text-[10px] font-medium text-[#64748B] uppercase">Premio fisico</label>
                    <input type="text" value={sr.reward} onChange={e => updateStreak(idx, 'reward', e.target.value)}
                      placeholder="es: Bibita gratis" className="w-full mt-1 bg-white border border-slate-200 rounded-lg px-2.5 py-1.5 text-sm text-[#1A1A1A] focus:border-[#D44E20] focus:outline-none" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* 4. Milestone badges */}
        <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm">
          <div className="flex items-center justify-between mb-1">
            <h3 className="font-semibold text-[#1A1A1A] flex items-center gap-2"><Trophy size={16} className="text-yellow-500" /> Badge Milestone</h3>
            <button onClick={addMilestone} data-testid="add-milestone" className="text-xs font-medium text-[#D44E20] flex items-center gap-1 hover:underline"><Plus size={14} /> Aggiungi</button>
          </div>
          <p className="text-[#64748B] text-xs mb-4">Badge assegnati al raggiungimento di un certo numero di partite totali</p>

          {data.milestone_badges.length === 0 && <p className="text-[#64748B] text-sm text-center py-4">Nessun badge milestone configurato</p>}

          <div className="space-y-3">
            {data.milestone_badges.map((mb, idx) => (
              <div key={idx} className="bg-gray-50 rounded-xl p-4 border border-slate-100 flex items-end gap-3" data-testid={`milestone-row-${idx}`}>
                <div className="flex-1">
                  <label className="text-[10px] font-medium text-[#64748B] uppercase">Partite richieste</label>
                  <input type="number" value={mb.threshold} onChange={e => updateMilestone(idx, 'threshold', e.target.value)}
                    className="w-full mt-1 bg-white border border-slate-200 rounded-lg px-2.5 py-1.5 text-sm text-[#1A1A1A] focus:border-[#D44E20] focus:outline-none" />
                </div>
                <div className="flex-1">
                  <label className="text-[10px] font-medium text-[#64748B] uppercase">Nome badge</label>
                  <input type="text" value={mb.badge} onChange={e => updateMilestone(idx, 'badge', e.target.value)}
                    placeholder="es: collezionista" className="w-full mt-1 bg-white border border-slate-200 rounded-lg px-2.5 py-1.5 text-sm text-[#1A1A1A] focus:border-[#D44E20] focus:outline-none" />
                </div>
                <button onClick={() => removeMilestone(idx)} className="text-red-400 hover:text-red-600 pb-1.5"><Trash2 size={14} /></button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
